/**
 * Per-field (and per-country) presentation + validation metadata for the
 * Alfred KYC fallback form. Keyed by Alfred fieldName; the descriptor's
 * country (ISO alpha-3, normalized to alpha-2 here) drives the rules.
 *
 * Validation is STRICT and country-specific — document/tax numbers must be
 * plain digits of an exact length (no dashes/spaces), phones must match the
 * country's digit count, DOB must be 18+, names a single word. Formats are
 * taken from what Alfred accepts in the dev presets; adjust if their spec
 * changes. Return an error string to show, or null when acceptable.
 */
export interface KycFieldMeta {
  /** Friendly label override (when the humanized fieldName is unclear, e.g. "pep"). */
  label?: string;
  placeholder?: string;
  tooltip?: string;
  /** Strips disallowed chars as the user types (e.g. dashes/spaces on a digit-only field). */
  sanitize?: (value: string) => string;
  maxLength?: number;
  validate?: (value: string) => string | null;
}

// Alfred KYC supports only these 4 countries. Descriptor country is alpha-3.
const ALPHA3_TO_2: Record<string, string> = { ARG: "AR", BRA: "BR", MEX: "MX", COL: "CO" };

const digitsOf = (v: string): string => v.replace(/\D/g, "");
const phoneSanitize = (v: string): string =>
  (v.trimStart().startsWith("+") ? "+" : "") + digitsOf(v);
const alnumUpper = (v: string): string => v.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

// National identity document (`dni` field = our document_number) per country.
const DNI_RULE: Record<
  string,
  { re: RegExp; msg: string; ph: string; san: (v: string) => string; max: number }
> = {
  AR: { re: /^\d{11}$/, msg: "Enter 11 digits — no spaces or dashes", ph: "20123456786", san: digitsOf, max: 11 },
  BR: { re: /^\d{11}$/, msg: "Enter 11 digits — no spaces or dashes", ph: "12345678909", san: digitsOf, max: 11 },
  MX: { re: /^[A-Za-z0-9]{18}$/, msg: "Enter the 18-character CURP", ph: "TUMR900115HDFXYZ09", san: alnumUpper, max: 18 },
  CO: { re: /^\d{10}$/, msg: "Enter 10 digits — no spaces or dashes", ph: "1020304050", san: digitsOf, max: 10 },
};

// Phone: total digit count (country code + national), digits only.
const PHONE_RULE: Record<string, { len: [number, number]; ph: string }> = {
  AR: { len: [12, 13], ph: "+5491140000000" },
  BR: { len: [12, 13], ph: "+5511900000000" },
  MX: { len: [12, 12], ph: "+525500000000" },
  CO: { len: [12, 12], ph: "+573000000000" },
};

const ZIP_PLACEHOLDER: Record<string, string> = {
  AR: "C1043AAQ",
  BR: "01310-100",
  MX: "01000",
  CO: "110111",
};

// Per-country national tax field (AR→cuit, BR→cpf) — always 11 digits.
const TAX_HINT: Record<string, { label: string; sample: string }> = {
  cuit: { label: "CUIT (Argentine tax ID)", sample: "20123456789" },
  cpf: { label: "CPF (Brazilian taxpayer ID)", sample: "12345678909" },
};

const SINGLE_WORD_NAME = /^[\p{L}\p{M}'-]+$/u; // one word, no spaces
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ageFrom = (iso: string): number | null => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age;
};

export const getKycFieldMeta = (fieldName: string, countryRaw = ""): KycFieldMeta => {
  const country = ALPHA3_TO_2[countryRaw.toUpperCase()] ?? countryRaw.toUpperCase();

  switch (fieldName) {
    case "firstName":
      return {
        placeholder: "Maria",
        tooltip: "Your legal first name, exactly as on your ID. A single word.",
        validate: (v) => (SINGLE_WORD_NAME.test(v) ? null : "Enter a single name, no spaces"),
      };
    case "lastName":
      return {
        placeholder: "Gomez",
        tooltip: "Your legal last name, exactly as on your ID. A single word.",
        validate: (v) => (SINGLE_WORD_NAME.test(v) ? null : "Enter a single surname, no spaces"),
      };
    case "dateOfBirth":
      return {
        tooltip: "Date of birth — you must be at least 18.",
        validate: (v) => {
          const age = ageFrom(v);
          if (age === null) return "Enter a valid date";
          if (age < 18) return "You must be at least 18";
          if (age > 120) return "Enter a valid date";
          return null;
        },
      };
    case "country":
      return {
        tooltip: "Your verified country — set during KYC and can't be changed here.",
      };
    case "state":
      return { placeholder: "Buenos Aires", tooltip: "State / province / region." };
    case "city":
      return { placeholder: "Buenos Aires", tooltip: "City of residence." };
    case "address":
      return { placeholder: "Av. Corrientes 1234", tooltip: "Residential address — street and number." };
    case "zipCode":
      return {
        placeholder: ZIP_PLACEHOLDER[country] ?? "Postal code",
        tooltip: "Postal / ZIP code for your address.",
      };
    case "dni": {
      const rule = DNI_RULE[country];
      return {
        placeholder: rule?.ph ?? "National ID number",
        tooltip: "Your national identity document number.",
        sanitize: rule?.san ?? digitsOf,
        maxLength: rule?.max ?? 20,
        validate: rule
          ? (v) => (rule.re.test(v) ? null : rule.msg)
          : (v) => (/^\d{5,20}$/.test(v) ? null : "Enter digits only"),
      };
    }
    case "phoneNumber": {
      const rule = PHONE_RULE[country];
      return {
        placeholder: rule?.ph ?? "+1...",
        tooltip: "Mobile number including the country code — digits only.",
        sanitize: phoneSanitize,
        maxLength: 16,
        validate: (v) => {
          if (!/^\+?\d+$/.test(v)) return "Digits only (no spaces or dashes)";
          const len = digitsOf(v).length;
          if (rule) {
            return len >= rule.len[0] && len <= rule.len[1]
              ? null
              : "Enter a valid phone for your country, with code";
          }
          return len >= 8 && len <= 15 ? null : "Enter a valid phone with country code";
        },
      };
    }
    case "email":
      return {
        placeholder: "name@example.com",
        tooltip: "Email address — used for verification updates.",
        validate: (v) => (EMAIL_RE.test(v) ? null : "Enter a valid email"),
      };
    case "pep":
      return {
        label: "Politically Exposed Person (PEP)",
        tooltip:
          "A Politically Exposed Person holds (or held) a prominent public role. Tick only if this applies to you.",
      };
    case "typeDocumentCol":
      return {
        placeholder: "CC",
        tooltip: "Colombian document type (e.g. CC = Cédula de Ciudadanía).",
      };
    default: {
      const tax = TAX_HINT[fieldName]; // cuit / cpf — 11 digits, no separators
      if (tax) {
        return {
          placeholder: tax.sample,
          tooltip: `${tax.label} — 11 digits, no spaces or dashes.`,
          sanitize: digitsOf,
          maxLength: 11,
          validate: (v) => (/^\d{11}$/.test(v) ? null : `${tax.label} must be 11 digits`),
        };
      }
      return {};
    }
  }
};
